/**
 * Contractor (1099) labor captured via AP bills.
 *
 * Steve's flow: a 1099 invoice may list multiple projects with hours and a
 * dollar value per project. The bill entry form already captures this on
 * `BillLine` (jobId + costType="labor" + optional hours/rate/amount).
 *
 * This selector aggregates those lines by job so reports (variance, job
 * profitability) can read contractor labor alongside internal labor —
 * WITHOUT touching the internal-crew `LaborActualRecord` stream (which is
 * loaded-rate based and must stay clean).
 */
import type { Bill } from "./apTypes";

export interface ContractorLaborByJob {
  jobId: string;
  dollars: number;
  hours: number;          // 0 when no bill line provided hours
  billCount: number;
}

export function getContractorLaborByJob(bills: Bill[]): Map<string, ContractorLaborByJob> {
  const byJob = new Map<string, ContractorLaborByJob>();
  for (const b of bills) {
    if (b.status === "void") continue;
    for (const l of b.lines) {
      if (l.costType !== "labor") continue;
      if (!l.jobId) continue;
      const cur = byJob.get(l.jobId) ?? { jobId: l.jobId, dollars: 0, hours: 0, billCount: 0 };
      cur.dollars += l.amount;
      cur.hours += l.hours ?? 0;
      cur.billCount += 1;
      byJob.set(l.jobId, cur);
    }
  }
  return byJob;
}

export function getContractorLaborForJob(
  jobId: string,
  bills: Bill[],
): ContractorLaborByJob {
  return (
    getContractorLaborByJob(bills).get(jobId) ?? {
      jobId, dollars: 0, hours: 0, billCount: 0,
    }
  );
}
